<template>
  <LayoutAppNavBar
    v-if="navigationFeatureFlag"
    :languages="navigationData?.languages"
    :nav-items="navigationData.navigationItems"
  />
  <div v-else ref="navigationHeader">
    <div class="navigation header">
      <NuxtLink :external="useStaticLinks" :href="localePath('/')" class="logo">
        <Logo />
      </NuxtLink>

      <div id="on-desktop">
        <nav class="main-nav">
          <ul>
            <li
              v-for="navItem in navItems"
              href=""
              class="nav-item-remover"
              :class="navItem.className"
              :key="navItem.id"
            >
              <NuxtLink
                v-if="typeof navItem.action === 'string'"
                :to="navItem.action"
                @click="trackMainNavi(navItem.name)"
                :data-testid="navItem.testId"
                :external="useStaticLinks"
              >
                {{ navItem.name }}
              </NuxtLink>
              <a
                v-else
                href=""
                @click.prevent="navItem.action(navItem)"
                :data-testid="navItem.testId"
              >
                {{ navItem.name }}
              </a>
            </li>
          </ul>
        </nav>
        <nav class="sub-nav">
          <ul>
            <li
              @click="toogleUserSettingDropdown('language-selection')"
              class="language-selection dropdown"
              data-testid="language-selector"
            >
              <div class="language-svg dropdown-button">
                <LoadingSpinner v-if="!languageSelectionOptions.length" />
                <svg
                  v-else
                  width="18"
                  height="18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 0a9 9 0 110 18A9 9 0 019 0zm3.342 9.75H9.75v6.011l.042-.053c1.535-2.008 2.38-3.992 2.55-5.958zm-8.025 0H1.914a7.132 7.132 0 004.592 5.926c-1.308-1.958-2.042-3.935-2.189-5.926zm11.769 0h-2.239c-.145 1.961-.859 3.91-2.13 5.84a7.133 7.133 0 004.369-5.84zm-7.836 0H5.822c.167 1.914.97 3.843 2.427 5.796L8.25 9.75zM6.506 2.323l-.016.007a7.132 7.132 0 00-4.576 5.92h2.403c.147-1.99.88-3.967 2.189-5.927zm3.244-.086V8.25h2.592c-.172-1.982-1.029-3.982-2.588-6.008l-.004-.005zm1.967.174l.216.337c1.137 1.82 1.778 3.656 1.914 5.503h2.239a7.131 7.131 0 00-4.369-5.84zm-3.468.044C6.792 4.408 5.99 6.337 5.822 8.25H8.25z"
                    fill="currentColor"
                    fill-rule="evenodd"
                  ></path>
                </svg>
              </div>
              <div
                v-if="languageSelectionOptions.length"
                class="dropdown-content inactive language-selection-dropdown"
                data-testid="language-selector-list"
              >
                <p class="languages-headline">
                  {{ $t("website.general.languages") }}
                </p>
                <a
                  v-for="option in languageSelectionOptions"
                  :class="{ active: option.shortCode == locale }"
                  :href="option.slug"
                  class="track_headernavigation"
                  :external="useStaticLinks"
                >
                  {{ option.name }}
                </a>
              </div>
            </li>
            <li
              v-for="navigationMetaItem in navigationMetaItems"
              :class="`${navigationMetaItem.className} ${
                navigationMetaItem.type == 'navigationItemDivider'
                  ? ' divider-main-menu'
                  : ''
              }`"
              class="dropdown user-item-remover"
              :key="navigationMetaItem.id"
              @click="navigationMetaItem.action(navigationMetaItem)"
            >
              <div
                v-if="navigationMetaItem.type !== 'navigationItemDivider'"
                class="sub-nav-item"
              >
                <div class="dropdown-button">
                  <a :data-testid="navigationMetaItem.testId">
                    {{ navigationMetaItem.name }}
                  </a>
                </div>
              </div>
              <div
                class="dropdown-content inactive"
                :class="`${navigationMetaItem.className}-dropdown`"
                v-show="navigationMetaItem.options"
              >
                <div v-for="option in navigationMetaItem.options">
                  <div
                    v-if="option.type == 'navigationItemDivider'"
                    class="horizontal-divider"
                  >
                    <hr />
                  </div>
                  <NuxtLink :to="option.link" external>
                    {{ option.label }}
                  </NuxtLink>
                </div>
              </div>
            </li>
            <li class="login dropdown user-item-remover">
              <div class="sub-nav-item">
                <LoadingSpinner v-if="!userLoaded" />
                <a
                  href="#"
                  v-else-if="isAuthenticated"
                  class="dropdown-button"
                  @click.prevent="loginOpen = !loginOpen"
                  data-testid="logged-in-button"
                >
                  {{ `Hi, ${userStore.firstName}` }}
                </a>
                <a
                  href="#"
                  v-else
                  class="dropdown-button"
                  @click.prevent="loginHandler"
                  data-testid="login-button"
                >
                  {{ $t("website.general.log_in") }}
                </a>
              </div>
              <div
                v-show="loginOpen"
                v-if="isAuthenticated"
                :class="{ inactive: !loginOpen, active: loginOpen }"
                class="dropdown-content login-dropdown"
                data-testid="logged-in-menu"
              >
                <div v-for="option in loggedInMetaItemOptions">
                  <NuxtLink
                    :to="option.slug.value"
                    :target="option.slug.newTab ? '_blank' : null"
                    :data-testid="option.testId"
                    :external="useStaticLinks"
                    @click="loginOpen = false"
                  >
                    {{ option.text }}
                  </NuxtLink>
                </div>
                <div class="horizontal-divider">
                  <hr />
                </div>
                <div>
                  <a
                    href="#"
                    @click.prevent="logoutHandler"
                    class="logout-button"
                    data-testid="logout-button"
                  >
                    {{ $t("website.general.logout") }}
                  </a>
                </div>
              </div>
            </li>
          </ul>
        </nav>
      </div>

      <button
        class="burger-box"
        aria-label="Main menu"
        :aria-expanded="mobileDrawerOpen"
        @click="toggleBurgerMenu"
      >
        <div class="burger">
          <div
            id="burger-nav-icon"
            :class="{
              open: mobileDrawerOpen,
            }"
          >
            <span></span>
            <span></span>
          </div>
        </div>
      </button>
    </div>
    <Transition>
      <div
        class="horizontal-drawer"
        v-if="drawerOpen"
        :data-testid="selectedSubnavigation['dataId']"
      >
        <div
          v-for="subNavigationHeader in selectedSubnavigation"
          class="subNavigationDrawer"
        >
          <NuxtLink
            class="subNavigationHeader"
            :to="subNavigationHeader.slug.value"
            :external="
              useStaticLinks || subNavigationHeader.slug.type != 'internal'
            "
            >{{ subNavigationHeader.text }}</NuxtLink
          >

          <div class="drawer-flex">
            <div
              v-for="firstLayer in subNavigationHeader.subNavigation"
              class="first-layer"
            >
              <div class="sub-content" v-if="useStaticLinks">
                <a
                  class="subNavigationSubHeader"
                  :href="firstLayer.slug.value"
                  >{{ firstLayer.text }}</a
                >
                <div v-for="secondLayer in firstLayer.subNavigation">
                  <a
                    class="subNavigationContent"
                    :href="secondLayer.slug.value"
                    >{{ secondLayer.text }}</a
                  >
                </div>
              </div>
              <div class="sub-content" v-else>
                <NuxtLink
                  class="subNavigationSubHeader"
                  :to="firstLayer.slug.value"
                  :external="useStaticLinks"
                  >{{ firstLayer.text }}</NuxtLink
                >
                <div v-for="secondLayer in firstLayer.subNavigation">
                  <NuxtLink
                    class="subNavigationContent"
                    :to="secondLayer.slug.value"
                    :external="useStaticLinks"
                    >{{ secondLayer.text }}</NuxtLink
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
    <Transition>
      <div id="mobile-drawer" v-if="mobileDrawerOpen">
        <nav class="main-nav">
          <div
            v-for="navItem in navItems"
            class="nav-item-remover accordion"
            :class="navItem.className"
            :key="navItem.id"
          >
            <div>
              <NuxtLink
                v-if="
                  typeof navItem.action === 'string' || !navItem.hasSubNavItems
                "
                :to="navItem.action"
                :external="useStaticLinks"
                @click="trackMainNavi(navItem.name)"
              >
                {{ navItem.name }}
              </NuxtLink>
              <a v-else href="" @click.prevent="toggleAccordion(navItem)">
                {{ navItem.name }}
              </a>
            </div>
            <svg
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              class="arrow"
              v-if="navItem.hasSubNavItems"
              :id="`${navItem.className}-accordion-arrows`"
            >
              <path
                d="M12 13.172L16.95 8.222L18.364 9.636L12 16L5.636 9.636L7.05 8.222L12 13.172Z"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>
            <div :class="`${navItem.className}-panel`" class="accordion-panel">
              <div
                v-for="subnavigation in subNavigationItems[navItem.className]"
              >
                <a
                  v-if="useStaticLinks"
                  class="accordion-menu-point"
                  :href="subnavigation.slug.value"
                >
                  {{ subnavigation.text }}
                </a>
                <div
                  v-else
                  class="accordion-menu-point"
                  @click="
                    () => {
                      trackMainNavi(subnavigation.text);
                      router.push(subnavigation.slug.value);
                    }
                  "
                >
                  {{ subnavigation.text }}
                </div>
                <div
                  v-for="navigationItem in subnavigation.subNavigation"
                  class="first-layer-content"
                >
                  <a
                    v-if="useStaticLinks"
                    class="accordion-menu-point"
                    :href="navigationItem.slug.value"
                  >
                    {{ navigationItem.text }}
                  </a>
                  <div
                    v-else
                    class="accordion-menu-point"
                    @click="
                      () => {
                        trackMainNavi(navigationItem.text);
                        router.push(navigationItem.slug.value);
                      }
                    "
                  >
                    {{ navigationItem.text }}
                  </div>
                  <div
                    v-for="item in navigationItem.subNavigation"
                    class="second-layer-content"
                  >
                    <NuxtLink
                      class="accordion-menu-point"
                      :to="item.slug.value"
                      :external="useStaticLinks"
                      @click="toggleBurgerMenu"
                    >
                      {{ item.text }}
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
        <div class="mobile-seperator"></div>
        <nav class="main-nav">
          <div
            class="accordion"
            @click="toggleAccordion({ className: 'language-selection' })"
          >
            <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 0a9 9 0 110 18A9 9 0 019 0zm3.342 9.75H9.75v6.011l.042-.053c1.535-2.008 2.38-3.992 2.55-5.958zm-8.025 0H1.914a7.132 7.132 0 004.592 5.926c-1.308-1.958-2.042-3.935-2.189-5.926zm11.769 0h-2.239c-.145 1.961-.859 3.91-2.13 5.84a7.133 7.133 0 004.369-5.84zm-7.836 0H5.822c.167 1.914.97 3.843 2.427 5.796L8.25 9.75zM6.506 2.323l-.016.007a7.132 7.132 0 00-4.576 5.92h2.403c.147-1.99.88-3.967 2.189-5.927zm3.244-.086V8.25h2.592c-.172-1.982-1.029-3.982-2.588-6.008l-.004-.005zm1.967.174l.216.337c1.137 1.82 1.778 3.656 1.914 5.503h2.239a7.131 7.131 0 00-4.369-5.84zm-3.468.044C6.792 4.408 5.99 6.337 5.822 8.25H8.25z"
                fill="currentColor"
                fill-rule="evenodd"
              ></path>
            </svg>
            <svg
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              class="arrow"
              id="language-selection-accordion-arrows"
            >
              <path
                d="M12 13.172L16.95 8.222L18.364 9.636L12 16L5.636 9.636L7.05 8.222L12 13.172Z"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>

            <div class="language-selection-panel accordion-panel">
              <div class="mobile-language-headline">
                {{ $t("language") }}
              </div>
              <NuxtLink
                v-for="option in languageSelectionOptions"
                :class="{ active: option.code == localeToCfLocal(locale) }"
                :to="option.slug"
                :external="useStaticLinks"
                @click="trackMainNavi(option.name)"
              >
                {{ option.name }}
              </NuxtLink>
            </div>
          </div>

          <div
            v-for="navigationMetaItem in navigationMetaItems"
            :class="`${navigationMetaItem.className} ${
              navigationMetaItem.type == 'navigationItemDivider'
                ? ' mobile-seperator'
                : ''
            }`"
            :key="navigationMetaItem.id"
            @click="navigationMetaItem.action(navigationMetaItem)"
          >
            <div
              v-if="navigationMetaItem.type !== 'navigationItemDivider'"
              class="sub-nav-item"
            >
              <div class="dropdown-button">
                <a>
                  {{ navigationMetaItem.name }}
                </a>
              </div>
            </div>
          </div>

          <div class="accordion" v-if="!isAuthenticated">
            <a href="#" @click.prevent="loginHandler">
              {{ $t("website.general.log_in") }}
            </a>
          </div>

          <div
            class="accordion"
            v-if="isAuthenticated"
            v-for="option in loggedInMetaItemOptions"
          >
            <NuxtLink
              :to="option.slug.value"
              :target="option.slug.newTab ? '_blank' : null"
              @click="trackMainNavi(option.text)"
              :external="useStaticLinks"
            >
              {{ option.text }}
            </NuxtLink>
          </div>

          <div class="accordion" v-if="isAuthenticated">
            <a
              href="#"
              @click.prevent="logoutHandler"
              class="logout-button"
              v-if="isAuthenticated"
              @click="trackMainNavi($t('website.general.logout'))"
            >
              {{ $t("website.general.logout") }}
            </a>
          </div>
        </nav>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { cfLocaleToLocal, localeToCfLocal } from "~/composables/useLocales";
import { fetchLanguages } from "~/services/masterdataService";
import { getLanguageUrl } from "~/services/navigationService";

import useTracking from "~/composables/useTracking";
import { useUserStore } from "~/stores/userStore";
import { NavigationId } from "~/types/navigation";
import useHotelsDeprecated from "~/composables/useHotelsDeprecated";
import type { Language, NavItem } from "@citizenm/layout/types/navbar.types.ts";
import Logo from "~/assets/icons/svgo/logo.svg?component";

defineProps<{
  navigationData: { navigationItems: NavItem[]; languages: Language[] };
}>();

const navigationFeatureFlag = useCookie("_ff_FOJO-2381-navigation");
/**
 * Temp solution to prevent showing unpublished hotels with static JSON
 *
 * TODO: replace with realtime data again
 * SEE: https://citizenm.atlassian.net/browse/FOJO-2381
 * NOTE: this array is now empty. We might need it for London again soon, so keep it for now.
 */
const UNPUBLISHED_HOTEL_IDS = [];

const userStore = useUserStore();
const { $auth, $contentfulClient, $contentfulMarketingClient, $isProd } =
  useNuxtApp();
const { locale, t } = useI18n();
const localeRoute = useLocaleRoute();
const localePath = useLocalePath();
const { logout, isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
  $auth;
const router = useRouter();
const route = useRoute();
const { trackMainNavi, trackUserInfo } = useTracking();
const { drawerOpen, mobileDrawerOpen } = useNavigationDeprecated();

const userLoaded = ref(false);
const loginOpen = ref(false);
const navigationHeader = ref(null);

const config = useRuntimeConfig();
const useStaticLinks = ref(!config.public.headless);

const mycitizenmNavi = useMycitizenmNavi();
const { list } = useHotelsDeprecated();

const head = useHead();

const { data: navigationResponse } = useFetch("/api/utilities/navigation", {
  query: {
    lang: locale.value,
  },
});

// Will be overwritten by API response
const loggedInMetaItemOptions = computed(() => {
  return mycitizenmNavi.map((naviItem) => {
    let testId = "navigation-logged-in-menu" + naviItem.to.replaceAll("/", "-");

    return {
      text: t(naviItem.title),
      slug: {
        value: localeRoute(naviItem.to, locale.value),
      },
      testId: testId,
    };
  });
});

function logoutHandler() {
  if (import.meta.client) {
    // Clear redfast data on explicit user logout
    localStorage.removeItem("redfast-id");
    localStorage.removeItem("redfast-user-email");

    userStore.logoutUser();
    logout({
      logoutParams: {
        returnTo: window.location.origin + localePath("/"), // Go to homepage with a message
      },
    });
  }
}

function loginHandler() {
  loginWithRedirect({
    appState: {
      target: route.fullPath,
    },
  });
}

const emits = defineEmits(["openDrawer", "closeDrawer"]);

const handleClickOutside = (event) => {
  event.stopPropagation();

  if (navigationHeader.value.contains(event.target)) return;

  if (drawerOpen.value) {
    drawerOpen.value = false;
    document.body.style.overflow = "auto";
    emits("closeDrawer");
    const navItemsElement = document.getElementsByClassName("nav-item-remover");

    for (let i = 0; i < navItemsElement.length; i++) {
      navItemsElement[i].classList.remove("active");
    }
  }

  if (loginOpen.value) loginOpen.value = false;

  if (userNavDropDowns["language-selection"]) {
    const userItemElement =
      document.getElementsByClassName("language-selection")[0];
    const userItemElementDropdown = document.getElementsByClassName(
      "language-selection-dropdown",
    )[0];
    const proceed =
      userItemElement.classList.contains("active") &&
      !userItemElementDropdown.classList.contains("inactive");
    if (proceed) {
      toogleUserSettingDropdown("language-selection");
    }
  }
  navigationMetaItems.value.forEach((userNavItem) => {
    if (userNavDropDowns[userNavItem.className]) {
      const userItemElement = document.getElementsByClassName(
        userNavItem.className,
      )[0];
      const userItemElementDropdown = document.getElementsByClassName(
        `${userNavItem.className}-dropdown`,
      )[0];
      const proceed =
        userItemElement.classList.contains("active") &&
        !userItemElementDropdown.classList.contains("inactive");
      if (proceed) {
        toogleUserSettingDropdown(userNavItem.className);
      }
    }
  });
};

const languageSelectionOptions = ref([]);
const navItems = ref([]);
const userNavDropDowns = ref({});
const navigationMetaItems = ref([]);

const selectedSubnavigation = ref();
const subNavigationItems = ref({});

const switchLocalePath = useSwitchLocalePath();

function toogleUserSettingDropdown(elementClassName) {
  const userNavItemElement = document.getElementsByClassName(elementClassName);
  for (let i = 0; i < userNavItemElement.length; i++) {
    userNavItemElement[i].classList.toggle("active");
    const dropdownContainer = document.getElementsByClassName(
      `${elementClassName}-dropdown`,
    );
    for (let j = 0; j < dropdownContainer.length; j++) {
      if (userNavDropDowns[elementClassName]) {
        dropdownContainer[j].classList.add("inactive");
        userNavDropDowns[elementClassName] = false;
      } else {
        dropdownContainer[j].classList.remove("inactive");
        setTimeout(() => {
          userNavDropDowns[elementClassName] = true;
        }, 100);
      }
    }
  }
}

function toggleBurgerMenu() {
  mobileDrawerOpen.value = !mobileDrawerOpen.value;
  document.body.style.overflow = mobileDrawerOpen.value ? "hidden" : "auto";
}

function toggleAccordion(navItem) {
  trackMainNavi(navItem.name);
  const elementClass = navItem.className;
  const relevantPanels = document.getElementsByClassName(
    `${elementClass}-panel`,
  )[0];
  const accordionArrow = document.getElementById(
    `${elementClass}-accordion-arrows`,
  );
  if (relevantPanels.style.display === "block") {
    accordionArrow.classList.remove("rotate-hundred-eighty-degrees");
    relevantPanels.style.display = "none";
  } else {
    relevantPanels.style.display = "block";
    accordionArrow.classList.add("rotate-hundred-eighty-degrees");
  }
}

function createClassName(name) {
  return name.toLowerCase().replace(/ /g, "-").replace(/\./g, "-");
}

function toggleDrawerAction(navItem) {
  const className = navItem.className;
  trackMainNavi(navItem.name);
  const navItemsElement = document.getElementsByClassName("nav-item-remover");
  for (let i = 0; i < navItemsElement.length; i++) {
    navItemsElement[i].classList.remove("active");
  }
  if (
    drawerOpen.value &&
    selectedSubnavigation.value === subNavigationItems.value[className]
  ) {
    drawerOpen.value = false;
    emits("closeDrawer");
    document.body.style.overflow = "auto";
    return;
  }
  drawerOpen.value = false;
  selectedSubnavigation.value = subNavigationItems.value[className];
  const hotelsMenuPoint = document.getElementsByClassName(className);
  hotelsMenuPoint[0].classList.toggle("active");
  setTimeout(() => {
    drawerOpen.value = true;
    emits("openDrawer");
    document.body.style.overflow = "hidden";
  }, 100);
}

function selectAction(navigationItem) {
  if (!navigationItem.subNavigation) {
    return navigationItem.slug.value;
  } else {
    let className = createClassName(navigationItem.text);
    subNavigationItems.value[className] = [];
    subNavigationItems.value[className]["dataId"] = navigationItem.testId;
    navigationItem.subNavigation.forEach((subNavigationItem) => {
      subNavigationItems.value[className].push(subNavigationItem);
    });

    return toggleDrawerAction;
  }
}

function selectMetaAction(metaNavigationItem) {
  if (!metaNavigationItem.subNavigation) {
    return () => {
      trackMainNavi(metaNavigationItem.text);
      if (useStaticLinks.value)
        return (location.href = metaNavigationItem.slug.value);

      router.push(metaNavigationItem.slug.value);
    };
  } else {
    return () => console.log("Missing meta action");
  }
}

async function setUpNavigation() {
  if (!navigationResponse.value) return;

  const navigationResponseData = navigationResponse.value.data;

  navigationMetaItems.value.forEach((subNavItem) => {
    userNavDropDowns.value[subNavItem.className] = false;
  });

  const newNavigationItems = [];

  navigationResponseData.mainNavigation.forEach((navigationItemThis, index) => {
    let href = navigationItemThis.slug.value;
    let testId = null;
    let testIdList = null;

    switch (index) {
      case 0:
        testId = "browse-hotel-by-city";
        testIdList = "browse-hotel-by-city-list";
        break;
      case 1:
        testId = "browse-hotel-by-city";
        testIdList = "browse-meeting-rooms-list";
        break;
    }
    navigationItemThis.testId = testIdList;

    if (
      navigationItemThis?.sys?.id === NavigationId.HotelByCity &&
      Array.isArray(navigationItemThis?.subNavigation)
    ) {
      /**
       * Filter unpublished hotels from subNavigation on PROD.
       * See Note above UNPUBLISHED_HOTEL_IDS
       */
      const filteredSubNav = navigationItemThis.subNavigation.reduce(
        (acc, current) => {
          const subNavigation = current.subNavigation?.filter(
            (subNavigationItem) =>
              !(
                $isProd &&
                UNPUBLISHED_HOTEL_IDS.includes(subNavigationItem.sys.id)
              ),
          );

          return [
            ...acc,
            {
              ...current,
              subNavigation,
            },
          ];
        },
        [],
      );

      list.value = filteredSubNav;
      navigationItemThis.subNavigation = filteredSubNav;
    }

    newNavigationItems.push({
      id: index,
      name: navigationItemThis.text,
      className: createClassName(navigationItemThis.text),
      action: selectAction(navigationItemThis),
      href,
      hasSubNavItems: !!navigationItemThis.subNavigation,
      testId: testId,
    });
  });

  navItems.value = newNavigationItems;

  const newNavigationMenuMetaItems = [];
  navigationResponseData.metaNavigation.forEach((navigationItem, index) => {
    newNavigationMenuMetaItems.push({
      id: index,
      name: navigationItem.text,
      type: navigationItem.sys.type,
      className: createClassName(navigationItem.text),
      action: selectMetaAction(navigationItem),
      options: [],
      testId: "navigation-" + navigationItem.text.replaceAll(" ", "-"),
    });
  });
  navigationMetaItems.value = newNavigationMenuMetaItems;
  loggedInMetaItemOptions.value = navigationResponseData.metaNavigationLoggedIn;
}

const { data: languages } = await useAsyncData(`languageOptions`, async () => {
  const cfLanguage = localeToCfLocal(locale.value);

  return await fetchLanguages(cfLanguage).then(
    (response) => response.data.languages,
  );
});

onMounted(async () => {
  try {
    const accessTokenResponse = await getAccessTokenSilently({
      detailedResponse: true,
    });
    if (isAuthenticated.value) {
      await userStore.loginUser(accessTokenResponse.id_token);
      userLoaded.value = true;
      userStore.setToken(accessTokenResponse.id_token);
      trackUserInfo({
        id: userStore.profileId || "",
        loginState: "loggedIn",
        account_type: userStore.getUserType || "consumer",
      });
    } else {
      throw new Error("Not logged in");
    }
  } catch (e) {
    userStore.removeToken();

    userLoaded.value = true;
    trackUserInfo({
      id: "",
      loginState: "anonymous",
      account_type: userStore.getUserType || "consumer",
    });
  }

  setUpNavigation();
  setupLanguageLinks();

  document.addEventListener("click", handleClickOutside);
  document.addEventListener("touchstart", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("touchstart", handleClickOutside);
});

const globalPageTypeId = useState("pagetypeId");

const setupLanguageLinks = async () => {
  if (globalPageTypeId.value) {
    const l = [];
    await Promise.allSettled(
      languages.value.map(async (lang) => {
        const langObj = {
          code: lang.code,
          name: lang.title,
          shortCode: cfLocaleToLocal(lang.code),
          slug: switchLocalePath(cfLocaleToLocal(lang.code)),
        };
        const url = await getLanguageUrl(
          langObj,
          globalPageTypeId.value,
          $contentfulMarketingClient,
          $contentfulClient,
          config.public,
          localePath,
        );
        if (url.slug) {
          l.push(url);
        }
      }),
    );
    languageSelectionOptions.value = l;
  } else {
    languageSelectionOptions.value = languages.value?.map((lang) => {
      return {
        code: lang.code,
        name: lang.title,
        shortCode: cfLocaleToLocal(lang.code),
        slug: switchLocalePath(cfLocaleToLocal(lang.code)),
      };
    });
  }

  // Canonicals and hreflang
  const link = [];
  // This is a hotfix to prevent rollback.
  // We have a ticket to re-address canonical building, which takes more time.
  // SEE: https://citizenm.atlassian.net/browse/WMD1-2328

  // TODO: Use defineI18nRoute compiler macro from nuxt i18n for slug/canonical building
  // Use top level useFetch to populate i18n paths
  const hosted = (url) => `${config.public.siteBaseUrl}${url}`;

  languageSelectionOptions.value.map((lang) => {
    if (lang.shortCode == "en") {
      link.push({
        rel: "alternate",
        hreflang: "x-default",
        href: hosted(lang.slug),
      });
      link.push({
        rel: "alternate",
        hreflang: lang.shortCode,
        href: hosted(lang.slug),
      });
    }
    if (lang.shortCode == locale.value) {
      link.push({
        rel: "canonical",
        href: hosted(lang.slug),
      });
    } else {
      link.push({
        rel: "alternate",
        hreflang: lang.shortCode,
        href: hosted(lang.slug),
      });
    }
  });

  head.patch({
    link,
  });
};

watch(
  () => locale,
  () => {
    setUpNavigation();
    setupLanguageLinks();
  },
  {
    immediate: true,
  },
);

// React to pageType and URL rendering
watch(globalPageTypeId, async () => {
  await setupLanguageLinks();
});
</script>

<style lang="scss" scoped>
.header {
  max-width: var(--max-width);
  margin: auto;
  height: var(--header-navigation-height);
  display: flex;
  align-items: flex-end;
  position: relative;
  margin-bottom: -14px;
  z-index: 66;

  @media screen and (max-width: 1024px) {
    padding-left: var(--space-24);
  }
}

.v-enter-active,
.v-leave-active {
  transition: transform 0.15s ease;
}

.v-enter-from,
.v-leave-to {
  transform: scaleY(0);
  opacity: 0;
}

.horizontal-drawer {
  background-color: var(--primary-white);
  width: 100%;
  margin: 0;
  padding: 0;
  padding-top: var(--space-20);
  z-index: 40;
  transform-origin: top center;

  a {
    color: inherit;
    text-decoration: none;
  }
}

.subNavigationDrawer {
  max-width: var(--max-width);
  margin: auto;
  padding: 0 var(--space-16);
}

.subNavigationDrawer .drawer-flex {
  display: flex;
  flex-wrap: wrap;
}

.sub-content {
  width: 200px;
  padding: 0 18px 20px 0;
}

.subNavigationHeader {
  color: var(--color-black-pressed);
  border-bottom: solid 1px var(--color-border);
  margin-bottom: 25px;
  font-size: var(--font-size-small);
  width: fit-content;
  cursor: pointer;
}

.subNavigationSubHeader {
  font-size: var(--font-size-small);
  font-family: var(--font-family-bold);
  margin: 0;
  cursor: pointer;
}

.subNavigationContent {
  font-size: var(--font-size-small);
  line-height: 20px;
  margin: 0;
  cursor: pointer;
}

.subNavigationSubHeader:hover {
  color: var(--primary-red);
}

.subNavigationContent:hover {
  color: var(--primary-red);
}

.horizontal-divider {
  margin: 0 !important;
  padding: 0 !important;
  margin-top: 20px !important;
}

.horizontal-divider hr {
  border-color: var(--color-border) solid 1px;
}

.dropdown-button {
  color: var(--primary-black);
  text-decoration: none;

  &:hover {
    color: var(--primary-red);
  }
}

.dropdown {
  position: relative;
  display: block;
  float: left;
  width: max-content;
}

.dropdown .inactive {
  display: none;
}

.dropdown-content {
  position: absolute;
  right: 0;
  top: 44px;
  color: var(--primary-black);
  background-color: var(--primary-white);
  padding: 20px 40px;
  width: max-content;
  height: max-content;
  box-shadow: var(--shadow-dropper);
  z-index: 1;
}

.dropdown-content div {
  padding: 3px 7px;
}

.dropdown-content a {
  text-decoration: none;
  color: var(--primary-black);
  font-family: var(--font-family-bold);
  font-size: var(--font-size-small);
}

.dropdown-content a:hover {
  color: var(--primary-red);
}

.dropdown-content .logout-button {
  color: var(--primary-black);
  font-family: var(--font-family-bold);
  font-size: var(--font-size-small);
}

.dropdown-content .logout-button:hover {
  color: var(--primary-red);
  cursor: pointer;
}

.language-selection-dropdown {
  font-size: var(--font-size-small);
  font-family: var(--font-family-bold);

  a {
    display: block;
  }
}

.language-selection-dropdown .languages-headline {
  font-family: var(--font-family);
  color: var(--color-black-pressed);
  margin-bottom: 25px;
  margin: 3px 7px 12px 7px;
}

.language-selection-dropdown a {
}

.language-selection-dropdown a:hover {
  color: var(--color-red-hover);
  cursor: pointer;
}

.language-selection-dropdown a.active {
  color: var(--color-red-hover);
}

#on-desktop {
  display: flex;
  width: 100%;
}

.logo {
  cursor: pointer;
  margin-right: var(--space-40);
  width: 56px;
  height: 56px;
  padding-bottom: 68px;
  text-align: center;
}

ul {
  list-style: none;
  display: flex;
  margin: 0;
  padding: 0;
  bottom: 0;
}

li {
  font-size: var(--font-size);
  line-height: 30px;
  height: 43px;
  cursor: pointer;
  transition: all 0.2s ease 0s;
  position: relative;
}

li::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  height: 1.5px;
  background-color: var(--color-red-hover);
  width: 0;
  transition: 0.2s;
}

li:hover::after {
  width: 100%;
}

li:hover {
  color: var(--color-red-hover);
}

li.active {
  color: var(--color-red-hover);
}

li.active::after {
  width: 100%;
}

.main-nav {
  flex-grow: 1;
  width: 100%;
}

.main-nav li {
  margin-left: var(--space-16);
  margin-right: var(--space-16);
  font-family: var(--font-family-bold);

  a {
    color: inherit;
    text-decoration: none;
  }
}

.vertical-seperator {
  height: 24px;
  width: 1.5px;
  margin-right: var(--space-12);
  background-color: gray;
  cursor: none;
}

.sub-nav {
  //position: absolute;
  right: var(--space-16);
}

.sub-nav-item {
  :deep(.spinner svg) {
    margin-top: 7px;
  }
}

.sub-nav li {
  display: flex;
  margin-left: var(--space-12);
  margin-right: var(--space-12);
}

.language-svg {
  margin-top: 5px;
}

.sub-nav .divider-main-menu {
  margin-left: 2px;
  margin-right: 12px;
}

.sub-nav .divider-main-menu::after {
  content: "";
  height: 20px;
  width: 1px;
  background-color: var(--color-light-grey-surface);
  margin-left: var(--space-6);
  margin-right: var(--space-12);
  position: absolute;
  top: 2px;
}

// ------------- MOBILE

.accordion {
  margin: var(--space-24) 0;
  width: 100%;
  position: relative;
  transition: 0.3ms;
  font-family: var(--font-family-bold);
}

.accordion .arrow {
  position: absolute;
  right: 0;
  top: 0;
  transition: 0.15s;
  pointer-events: none;
}

.accordion a {
  text-decoration: none;
  color: var(--primary-black);
  display: block;
}

.accordion a.active,
.accordion a:hover {
  color: var(--color-red-hover);
}

.rotate-hundred-eighty-degrees {
  transition: 0.15s;
  transform: rotate(180deg);
}

.accordion:hover {
  color: var(--primary-red);
  cursor: pointer;
}

.accordion-panel {
  font-family: var(--font-family);
  padding-top: 20px;
  padding-bottom: 20px;
  transform-origin: top center;
  display: none;
}

.accordion-panel > * {
  color: #5a5a5a;
  font-size: var(--font-size-small);
  margin-bottom: 14px;
}

.accordion-panel .language-option {
  color: var(--primary-black);
  font-family: var(--font-family-bold);
}

.accordion-panel .language-option.active {
  color: var(--color-red-hover);
}

.accordion-panel .first-layer-content {
  color: var(--primary-black);
  font-family: var(--font-family-bold);
  margin: var(--space-12) 0;
}

.accordion-panel .second-layer-content {
  color: var(--primary-black);
  font-family: var(--font-family);
}

.accordion-menu-point {
  display: block;
  padding-top: var(--space-12);
  padding-bottom: var(--space-12);
}

.accordion-menu-point:hover {
  color: var(--color-red-hover);
}

.mobile-language-headline {
  font-family: var(--font-family-bold);
}

.mobile-seperator {
  height: 1px;
  width: var(--space-72);
  background-color: var(--color-black-focus);
  margin-top: var(--space-16);
  margin-bottom: var(--space-16);
}

.burger-box {
  width: 48px;
  height: 48px;
  padding-top: 18px;
  cursor: pointer;
  position: absolute;
  top: 6px;
  right: 12px;
}

.burger {
  width: 24px;
  height: 12px;
  position: relative;
  margin: auto;
}

#burger-nav-icon span {
  background-color: black;
  position: absolute;
  transition: 0.3s cubic-bezier(0.8, 0.5, 0.2, 1.4);
  width: 100%;
  height: 2px;
}

#burger-nav-icon span:nth-child(1) {
  top: 0px;
  left: 0px;
}

#burger-nav-icon span:nth-child(2) {
  bottom: 0px;
  left: 0px;
}

.burger-box:hover > .burger > #burger-nav-icon span {
  background-color: var(--color-red-hover);
}

#burger-nav-icon.open span:nth-child(1) {
  transform: rotate(45deg);
  top: 6px;
}

#burger-nav-icon.open span:nth-child(2) {
  transform: rotate(-45deg);
  top: 6px;
}

#mobile-drawer {
  transform-origin: top center;
  width: 100vw;
  height: min(calc(100vh - 60px), calc(100vh - 60px)); // min header height
  background-color: var(--primary-white);
  padding: var(--space-24) var(--space-24) var(--space-120);
  overflow-y: scroll;
}

#mobile-drawer .main-nav ul {
  display: inline;
}

#mobile-drawer .main-nav ul li {
  font-size: var(--font-size-btn);
  padding: var(--space-24) 0 var(--space-24) 0;
  margin: 0;
}

#mobile-drawer .main-nav ul li::after {
  opacity: 0;
}

@media screen and (min-width: 1201px) {
  .burger-box {
    display: none;
    height: 0;
  }

  #mobile-drawer {
    display: none;
  }
}

@media screen and (max-width: 1200px) {
  #on-desktop {
    display: none;
  }

  .header {
    margin-bottom: -20px;
  }

  .horizontal-drawer {
    display: none;
  }

  .logo {
    cursor: pointer;
    margin-right: var(--space-40);
    width: 35px;
    height: 35px;
    padding-bottom: 68px;
    text-align: center;
  }
}
</style>
